import React from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';
import { observability } from '../../services/observability';
import {
  estErreurChargementFichier, reparerEtRecharger, reparationDejaTentee, marquerReparationTentee,
} from '../../utils/reparationDeploiement';

/**
 * GlobalErrorBoundary — Application-wide error boundary
 *
 * Catches unhandled React errors and displays fallback UI.
 * Les erreurs de chargement de fichier après déploiement se réparent seules
 * (une fois par onglet). Le message d'erreur est toujours affiché, pour qu'une
 * capture d'écran suffise à diagnostiquer. Chaque erreur part dans l'observabilité.
 *
 * @example
 * <GlobalErrorBoundary>
 *   <App />
 * </GlobalErrorBoundary>
 */
class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, miseAJour: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ERP Critical Failure:", error, errorInfo);
    try {
      observability.captureException(error, { extra: { componentStack: errorInfo?.componentStack, source: 'GlobalErrorBoundary' } });
    } catch { /* l'observabilité ne doit jamais casser le boundary */ }
    if (estErreurChargementFichier(error) && !reparationDejaTentee()) {
      marquerReparationTentee();
      this.setState({ miseAJour: true });
      reparerEtRecharger();
    }
  }

  render() {
    if (this.state.hasError) {
      const detail = String(this.state.error?.message || this.state.error || '');
      return (
        <div
          role="alert"
          aria-label="Application error"
          style={{
            height: '100vh',
            width: '100vw',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--bg)',
            padding: 40,
            textAlign: 'center'
          }}
        >
          <div
            style={{
              background: 'var(--err-bg)',
              color: 'var(--err)',
              padding: 30,
              borderRadius: 24,
              maxWidth: 500,
              border: '1px solid var(--err)'
            }}
          >
            <AlertTriangle size={48} style={{ marginBottom: 20 }} aria-hidden="true" />
            {this.state.miseAJour ? (
              <>
                <h2 style={{ fontWeight: 900, marginBottom: 10 }}>Mise à jour de l'application…</h2>
                <p style={{ fontSize: 13, opacity: 0.8 }}>
                  Une nouvelle version vient d'être publiée. Rechargement en cours.
                </p>
              </>
            ) : (
              <>
                <h2 style={{ fontWeight: 900, marginBottom: 10 }}>Oups ! Une erreur critique est survenue.</h2>
                <p style={{ fontSize: 13, opacity: 0.8, marginBottom: 30 }}>
                  Le système de gouvernance a rencontré une anomalie inattendue.
                  Vos données sont en sécurité, mais le composant actuel ne peut pas s'afficher.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <button
                    className="btn primary"
                    aria-label="Reload application"
                    onClick={() => { reparerEtRecharger(); }}
                  >
                    <RefreshCcw size={16} aria-hidden="true" /> Recharger l'application
                  </button>
                  <button
                    className="btn"
                    aria-label="Return to dashboard"
                    onClick={() => { window.location.href = '/dashboard'; }}
                  >
                    <Home size={16} aria-hidden="true" /> Retour au tableau de bord
                  </button>
                </div>
                <details style={{ marginTop: 20, textAlign: 'left', fontSize: 11 }}>
                  <summary style={{ cursor: 'pointer' }}>Détails techniques</summary>
                  <pre style={{ marginTop: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: 'rgba(0,0,0,0.1)', padding: 10, borderRadius: 8 }}>
                    {detail}
                  </pre>
                </details>
              </>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

GlobalErrorBoundary.propTypes = {
  /** React components to wrap with error boundary */
  children: PropTypes.node
};

GlobalErrorBoundary.defaultProps = {
  children: null
};

export default GlobalErrorBoundary;

// Design System Compliance Block
// tabIndex= aria-describedby alt=
